import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaRegCircle } from "react-icons/fa";

import DisplayQuestionCard from "../../../sub-components/managerQueSub/DisplayQuestionCard";
import CreateQueBoilerPlateCard from "../../../sub-components/managerQueSub/CreateQueBoilerPlateCard";
import CreateQuestionnaireTitle from "../../../../InterstellrComponents/CreateQuestionnaireTitle";

import {
  CREATE_QUESTIONNIARE,
  GET_ORG_QUESTIONNAIRE_DETAILS,
} from "../../../../Routes";
import { generateURLOptions } from "../../../../URLOptions";
import moment from "moment";

/* ----------------------------------------
   MAPPERS
---------------------------------------- */

const mapApiQuestionToUI = (q) => ({
  id: q._id,
  type:
    q.type === 0
      ? "mood"
      : q.type === 1
      ? "single"
      : q.type === 2
      ? "multi"
      : "text",
  text: q.title,
  answers:
    q.type === 1 || q.type === 2
      ? (q.options ?? []).map((opt) => ({
          id: crypto.randomUUID(),
          text: opt.title,
        }))
      : [],
});

const createEmptyQuestion = () => ({
  id: crypto.randomUUID(),
  type: "single",
  text: "",
  answers: [{ id: crypto.randomUUID(), text: "" }],
});

/* ----------------------------------------
   COMPONENT
---------------------------------------- */

const OrganisationEditDraftQuestionnaire = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // ✅ URL param

  const [title, setTitle] = useState("");
  const [endDate, setEndDate] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ----------------------------------------
     FETCH QUESTIONNAIRE
  ---------------------------------------- */

  useEffect(() => {
    if (!id) return;

    const fetchQuestionnaire = async () => {
      try {
        const response = await axios.get(GET_ORG_QUESTIONNAIRE_DETAILS + id, {
          headers: generateURLOptions(),
        });

        if (response.data.success === false) {
          alert(response.data.message ?? "");
          return;
        }

        const data = response.data.data;

        setTitle(data?.name ?? data?.title ?? "");
        setQuestions(data.questions.map(mapApiQuestionToUI));

        // ✅ THIS WAS MISSING
        setEndDate(
          data?.availableUntil ? moment(data.availableUntil).toDate() : null
        );

        setLoading(false);
      } catch (error) {
        console.error("Fetch questionnaire failed:", error);
        alert("Failed to fetch questionnaire");
        navigate("/manager/questionnaires");
      }
    };

    fetchQuestionnaire();
  }, [id, navigate]);

  /* ----------------------------------------
     QUESTION HANDLERS
  ---------------------------------------- */

  const updateQuestion = (updated) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === updated.id ? updated : q))
    );
  };

  const removeQuestion = (id) => {
    setQuestions((prev) =>
      prev.length > 1 ? prev.filter((q) => q.id !== id) : prev
    );
  };

  const addQuestion = () => {
    setQuestions((prev) => [...prev, createEmptyQuestion()]);
  };

  /* ----------------------------------------
     SUBMISSION (LOG ONLY FOR NOW)
  ---------------------------------------- */

  const buildPayload = (isDraft) => ({
    questionnaireId: id,
    title,
    endTime: endDate.toISOString(),
    draft: isDraft,
    questions: questions
      .filter((q) => q.text.trim())
      .map((q) => ({
        title: q.text,
        type:
          q.type === "mood"
            ? 0
            : q.type === "single"
            ? 1
            : q.type === "multi"
            ? 2
            : 3,
        options: q.type === "text" ? [] : q.answers.map((a) => a.text),
      })),
  });

  const saveDraft = async () => {
    if (!endDate) {
      return alert("Please select an End Date");
    }
    const payload = buildPayload(true);
    const response = await axios.post(CREATE_QUESTIONNIARE, payload, {
      headers: generateURLOptions(),
    });

    console.log("RESPONSE IS: ", response.data);

    if (response.data.success == false) {
      return alert(response?.message ?? "");
    }

    navigate("/manager/questionnaires");
  };

  const publish = async () => {
    if (!endDate) {
      return alert("Please select an End Date");
    }
    const payload = buildPayload(false);
    const response = await axios.post(CREATE_QUESTIONNIARE, payload, {
      headers: generateURLOptions(),
    });

    console.log("RESPONSE IS: ", response.data);

    if (response.data.success == false) {
      return alert(response?.message ?? "");
    }

    navigate("/manager/questionnaires");
  };

  /* ----------------------------------------
     RENDER
  ---------------------------------------- */

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-mpPurple/30 border-t-mpPurple animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen w-full"
      style={{
        background:
          "linear-gradient(45deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.85) 45%, rgba(81,83,245,1) 150%)",
      }}
    >
      <div className="container mx-auto pt-24 px-6 md:px-0 max-w-4xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center mb-8">
          <h1 className="text-3xl font-semibold text-black">
            Edit Questionnaire
          </h1>

          <div className="flex-1" />

          <div className="flex gap-3 mt-4 md:mt-0">
            <button
              onClick={() => navigate("/manager/questionnaires")}
              className="px-5 py-3 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              Discard
            </button>

            <button
              onClick={saveDraft}
              className="px-5 py-3 rounded-xl bg-slate-200 text-black hover:bg-slate-300"
            >
              Save Draft
            </button>

            <button
              onClick={publish}
              className="px-5 py-3 rounded-xl bg-mpPurple text-white hover:opacity-90"
            >
              Publish
            </button>
          </div>
        </div>

        {/* Title + Date */}
        <CreateQuestionnaireTitle
          title={title}
          setTitle={setTitle}
          endDate={endDate}
          handleDateChange={setEndDate}
        />

        {/* Preview */}
        <div className="flex flex-col gap-4 mb-10">
          {questions.map((q) => (
            <DisplayQuestionCard
              key={q.id}
              queType={q.type}
              queText={q.text}
              answersSet={q.answers}
            />
          ))}
        </div>

        {/* Editable Questions */}
        <div className="flex flex-col gap-4">
          {questions.map((q) => (
            <CreateQueBoilerPlateCard
              key={q.id}
              question={q}
              onChange={updateQuestion}
              onRemove={removeQuestion}
            />
          ))}
        </div>

        {/* Add Question */}
        <button
          onClick={addQuestion}
          className="flex items-center gap-2 mt-8 px-6 py-4 rounded-xl bg-mpPurple text-white font-semibold hover:opacity-90"
        >
          <FaRegCircle />
          Add Question
        </button>

        <div className="h-24" />
      </div>
    </div>
  );
};

export default OrganisationEditDraftQuestionnaire;
